import { OI_OHLC_FIELDS } from '@/features/chart/lib/config/chart-analytics'
import { CHART_ANALYTICS_DATA_KEYS } from '@/features/chart/lib/config/chart-analytics-data'
import type { ChartAnalyticsMetric } from '@/types/dashboard'
import type { CandleList } from '@/types/generated/public_api'

// Shared candle -> datafeed-analytics mapping used by both the historical
// provider and the live bridge, so per-bar analytics stay identical across the
// history/live boundary.

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'string') {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
  }

  return null
}

function getArrayValue(values: unknown, index: number): unknown {
  return Array.isArray(values) ? values[index] : undefined
}

function assignNumericAnalyticsValue<T extends string>(
  target: Partial<Record<T, number>>,
  key: T,
  values: unknown,
  index: number
): void {
  const numericValue = toFiniteNumber(getArrayValue(values, index))
  if (numericValue !== null) {
    target[key] = numericValue
  }
}

// OI OHLC studies read the raw field-name keys directly (open/high/low/close),
// so register every OI column under its own name alongside the liq columns.
const RAW_ANALYTICS_FIELD_MAP: Record<string, keyof CandleList> = {
  buyLiqBase: 'buyLiqBase',
  sellLiqBase: 'sellLiqBase',
}

for (const fields of Object.values(OI_OHLC_FIELDS)) {
  if (!fields) continue
  for (const fieldName of Object.values(fields)) {
    RAW_ANALYTICS_FIELD_MAP[fieldName] = fieldName
  }
}

export interface BarAnalytics {
  analytics: Partial<Record<ChartAnalyticsMetric, number>>
  rawAnalytics: Partial<Record<string, number>>
}

/** Pull the analytics + rawAnalytics for candle row `index` into the shape the
 *  datafeed caches and the TradingView studies read. */
export function extractBarAnalytics(candles: CandleList, index: number): BarAnalytics {
  const analytics: Partial<Record<ChartAnalyticsMetric, number>> = {}
  const rawAnalytics: Partial<Record<string, number>> = {}

  for (const [metric, field] of Object.entries(CHART_ANALYTICS_DATA_KEYS) as Array<
    [ChartAnalyticsMetric, keyof CandleList]
  >) {
    assignNumericAnalyticsValue(analytics, metric, candles[field], index)
  }

  for (const [metric, field] of Object.entries(RAW_ANALYTICS_FIELD_MAP) as Array<
    [string, keyof CandleList]
  >) {
    assignNumericAnalyticsValue(rawAnalytics, metric, candles[field], index)
  }

  return { analytics, rawAnalytics }
}
